import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';

export const CELL_PADDING = { meta: { padding: '16px 12px' } };
export const CELL_PADDING_LEFT = { meta: { padding: '16px 12px 16px 24px' } };
export const CELL_PADDING_LEFT_FIXED = { meta: { padding: '16px 12px 16px 24px', fixedWidth: true } };
export const CELL_PADDING_FIXED = { meta: { padding: '16px 12px', fixedWidth: true } };
export const STRETCH_CELL = { meta: { padding: '16px 12px', style: { alignSelf: 'stretch' } } };
export const STRETCH_CELL_FIXED = { meta: { padding: '16px 12px', fixedWidth: true, style: { alignSelf: 'stretch' } } };

export function buildEntityIdMap(rollups: AnalyticsResponseRollupCollection): Map<string, string> {
  const idByName = new Map<string, string>();
  for (const row of rollups.analyticsResponse.responseData) {
    if ((row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0) {
      const rawName = row.dimensionValues[0]?.value;
      const rawId = row.dimensionValues[1]?.value;
      const name = rawName != null ? String(rawName) : 'None';
      const id = rawId != null ? String(rawId) : 'null';
      if (name) idByName.set(name, id);
    }
  }
  return idByName;
}

// Rename "None" entity names to a display label (e.g. "Other" for payer source rows with no plan)
export function renameNoneEntity(name: string, noneDisplayName?: string): string {
  return name === 'None' && noneDisplayName ? noneDisplayName : name;
}

type EntityRow = { entityId: string; entityName: string };

export function buildEntityNameColumn<T extends EntityRow>(
  entityHeader: string,
  onEntityClick?: (entityId: string, entityName: string) => void
): ColumnDef<T, any> {
  const helper = createColumnHelper<T>();
  return helper.accessor('entityName' as any, {
    header: entityHeader,
    cell: (info) =>
      onEntityClick ? (
        <EntityName
          type='button'
          onClick={() => onEntityClick(info.row.original.entityId, info.row.original.entityName)}>
          {info.getValue() as string}
        </EntityName>
      ) : (
        <>{info.getValue() as string}</>
      ),
    size: 320,
    enableSorting: true,
    ...CELL_PADDING_LEFT_FIXED,
  });
}

export const EntityName = styled.button`
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
  width: calc(100% + 36px);
  margin: -16px -12px -16px -24px;
  padding: 16px 12px 16px 24px;
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18px;
  white-space: normal;
  overflow-wrap: anywhere;

  td:hover & {
    text-decoration: underline;
  }
`;

export const GreenText = styled.span`
  color: ${({ theme }) => theme.colors.accentGreen};
  background-color: ${colors.accentGreen05};
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
`;

export const RedText = styled.span`
  color: ${({ theme }) => theme.colors.accentRed};
  background-color: ${colors.accentRed05};
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
  width: 100%;
`;
